/**
 * Shared pieces for the two admin surfaces that author a KPI's data source:
 * the Formula tab (Beacon4PerformanceNetworkOverviewAdminFormulaPanel) and the
 * New/Edit/Clone dialog (Beacon4PerformanceNetworkOverviewAdminMetadataDialog).
 *
 * Both need the same column-field list, the same auto-fill heuristic and the
 * same insert-at-caret behaviour. Keeping one copy here is deliberate: a
 * duplicated auto-fill map would drift the first time a naming convention
 * changed, and the two screens would silently disagree about what a KPI's
 * date column is.
 */

// The seven column fields, in the order both screens display them. `viewName`
// is not among them — it comes from the curated source catalog.
export const COLUMN_FIELDS = [
  { key: 'dateCol',       label: 'Date column' },
  { key: 'sideCol',       label: 'Side column' },
  { key: 'polygonCol',    label: 'Polygon column' },
  { key: 'jvidCol',       label: 'Site (JV ID) column' },
  { key: 'mycomCol',      label: 'Mycom column' },
  { key: 'vendorCol',     label: 'Vendor column' },
  { key: 'importDateCol', label: 'Import date column' },
]

// Column names each field is usually backed by, in order of preference.
//
// This is a heuristic by design. The catalog stores no per-column role, so
// there is nothing authoritative to read — the alternative was a second table
// that someone would have to maintain by hand for every view. It only ever
// fills fields that are empty and every field stays editable, so a wrong guess
// costs one correction rather than silently breaking a KPI.
export const AUTOFILL_CANDIDATES = {
  dateCol:       ['Date'],
  sideCol:       ['Side'],
  polygonCol:    ['Polygon4', 'Polygon'],
  jvidCol:       ['JV_ID', 'JVID'],
  mycomCol:      ['MI_MYCOM_ID', 'MYCOM_ID', 'Mycom_id'],
  vendorCol:     ['VENDOR'],
  importDateCol: ['IMPORT_DATE'],
}

// Functions inserted as a skeleton rather than as `NAME()`, with the caret
// parked where the user will type first.
export const FUNCTION_TEMPLATES = {
  CASE: { text: 'CASE WHEN  THEN  ELSE  END', caret: 'CASE WHEN '.length },
}

// Drag payload type. Plain text so a token can also be dropped into any other
// text input without special handling.
export const DRAG_MIME = 'text/plain'

/** Catalog dropdown label: description first, raw view name as fallback. */
export const catalogLabel = (entry) => entry?.description?.trim() || entry?.viewName || ''

/** What a function chip drags as. */
export const functionDragText = (fn) => FUNCTION_TEMPLATES[fn]?.text ?? `${fn}()`

/** Token + caret offset a function chip inserts when clicked. */
export const functionInsertion = (fn) => {
  const tpl = FUNCTION_TEMPLATES[fn]
  return tpl ? { text: tpl.text, caret: tpl.caret } : { text: `${fn}()`, caret: fn.length + 1 }
}

/**
 * Works out which column-mapping fields can be filled from a view's columns.
 *
 * Pure on purpose — computing this inside a setState updater only appears to
 * work thanks to React's eager-state optimisation, and StrictMode invokes
 * updaters twice, which doubles any counter mutated in there.
 *
 * @param {object} form  current form values
 * @param {Array<{columnName: string}>} cols  columns of the selected view
 * @returns {{patch: object, detected: number, empty: number}}
 */
export const computeAutoFill = (form, cols) => {
  const byLower = new Map((cols ?? []).map(c => [c.columnName.toLowerCase(), c.columnName]))
  const patch = {}

  for (const { key } of COLUMN_FIELDS) {
    // Never overwrite something the user typed or that was already saved.
    if (form?.[key]?.trim()) continue
    const hit = (AUTOFILL_CANDIDATES[key] ?? [])
      .map(c => byLower.get(c.toLowerCase()))
      .find(Boolean)
    if (hit) patch[key] = hit
  }

  const detected = Object.keys(patch).length
  const empty = COLUMN_FIELDS.filter(({ key }) => !patch[key] && !form?.[key]?.trim()).length
  return { patch, detected, empty }
}

/**
 * Splices a token into a string, returning the new value and where the caret
 * should end up.
 *
 * @param {string} current
 * @param {number} start  selection start (replaced range begins here)
 * @param {number} end    selection end
 * @param {string} token
 * @param {number|null} caretOffset  offset within the token, or null for its end
 */
export const spliceToken = (current, start, end, token, caretOffset = null) => {
  const base = current ?? ''
  const from = Number.isInteger(start) ? start : base.length
  const to   = Number.isInteger(end) ? end : from
  return {
    value: base.slice(0, from) + token + base.slice(to),
    caret: from + (caretOffset ?? token.length),
  }
}

/** Chip label text for the auto-fill result. */
export const autoFillLabel = ({ detected, empty }) =>
  empty > 0 ? `${detected} auto-filled, ${empty} still empty` : `${detected} auto-filled`
